/* "Who's at the desk?" — pops on first /agent-desk visit if no agent
 * identity is set, and reopens when the agent clicks Switch.
 *
 * The dropdown is populated from /api/agents (rows from the agents
 * table whose `kind = 'human'` and `sip_username IS NOT NULL`). One
 * row per human, one Telnyx SIP credential per row — picking the row
 * is what gives the softphone the credentials it registers with. The
 * earlier "type a freeform name" UI minted a fresh browser_id per
 * tab and let two tabs share one credential, which is what was
 * dropping inbound calls after one ring.
 *
 * Real SSO is a follow-up. Today's posture is internal-trust: the
 * dropdown is the picker, and the row id is the audit identity. */

import { useEffect, useRef, useState, type FormEvent } from "react";

import { API_URL } from "../lib/api";

type AgentRow = { id: number; name: string; email: string | null };

export function SignInModal({
  initialAgentId,
  onSubmit,
  onCancel,
}: {
  initialAgentId: number | null;
  onSubmit: (agentId: number, name: string) => void;
  /** Provided only when there's already an identity to fall back to —
   *  cancelling a Switch flow shouldn't leave the user stranded. */
  onCancel?: () => void;
}) {
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | "">(
    initialAgentId ?? "",
  );
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/agents`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as { agents: AgentRow[] };
        if (cancelled) return;
        setAgents(j.agents);
        // If the persisted identity is still in the list, default to
        // it; otherwise default to the first agent so the dropdown
        // isn't empty.
        if (j.agents.length === 0) return;
        const stillPresent =
          initialAgentId !== null &&
          j.agents.some((a) => a.id === initialAgentId);
        setSelectedId(stillPresent ? initialAgentId! : j.agents[0]!.id);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialAgentId]);

  useEffect(() => {
    selectRef.current?.focus();
  }, [agents]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (selectedId === "" || agents === null) return;
    const row = agents.find((a) => a.id === selectedId);
    if (!row) return;
    onSubmit(row.id, row.name);
  }

  return (
    <div
      id="agent-desk-signin-overlay"
      className="agent-desk-signin-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-desk-signin-title"
    >
      <div className="agent-desk-signin-card">
        <h2 id="agent-desk-signin-title" className="agent-desk-signin-title">
          Who's at the desk?
        </h2>
        <p className="agent-desk-signin-body">
          Pick yourself from the agents list. Your row determines which
          Telnyx SIP credential the browser softphone registers with, so
          one human is one registered session — calls land on the right
          desk every time.
        </p>
        <form className="agent-desk-signin-form" onSubmit={submit}>
          <label
            className="agent-desk-signin-label"
            htmlFor="agent-desk-signin-input"
          >
            Agent
          </label>
          {loadError && (
            <div className="agent-desk-signin-err">
              Couldn't load agents list: {loadError}
            </div>
          )}
          {agents !== null && agents.length === 0 && !loadError && (
            <div className="agent-desk-signin-err">
              No agents provisioned yet. Add a row to the agents table
              with a Telnyx SIP credential before signing in.
            </div>
          )}
          <select
            ref={selectRef}
            id="agent-desk-signin-input"
            className="agent-desk-signin-input"
            value={selectedId}
            onChange={(e) =>
              setSelectedId(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={agents === null || agents.length === 0}
            required
          >
            {agents === null && <option value="">Loading…</option>}
            {agents !== null &&
              agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.email ? ` (${a.email})` : ""}
                </option>
              ))}
          </select>
          <div className="agent-desk-signin-actions">
            {onCancel && (
              <button
                id="agent-desk-signin-cancel-btn"
                type="button"
                className="agent-desk-signin-cancel"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
            <button
              id="agent-desk-signin-submit-btn"
              type="submit"
              className="agent-desk-signin-submit"
              disabled={selectedId === "" || agents === null || agents.length === 0}
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
